/**
 * useNsfOdOverride — Supervisor-gated NSF / Overdraft override on a DDA.
 *
 * Legacy procedures:
 *   - `NEWOD.pdf` (new overdraft limit)
 *   - `NEWESI.pdf` (essential overdraft)
 *   - `NOFUND.pdf`
 *   - `Back on Track.pdf`
 *   - `Overdraft Protection.pdf`
 *
 * Flow:
 *   1. Guard: user must be Supervisor+, OR a lesser role WITH supervisor
 *      approval captured (username + initials + timestamp).
 *   2. AcctMod Fields map — one or more of:
 *        NSFCount (reset counter)
 *        OdLimit  (new limit amount)
 *        OdExempt (yes/no)
 *        OdFeeWaiver (yes/no)
 *   3. CustMsgAdd audit note tagged with override reason + approver.
 *   4. Session events: acct.nsf.override.requested, acct.nsf.override.applied.
 *   5. If amount > $500 → auto-task "Compliance review" (priority 2).
 */

import { useCallback } from 'react';
import type { AcctModRequest } from '@retail-ops/shared-types';
import type { AcctType } from '@retail-ops/shared-types/enums';
import { useApi } from '../../hooks/useApi';
import { useEmitEvent } from '../../hooks/useEmitEvent';
import type { MaintActionResult } from '../types';

export type NsfOverrideType =
  | 'fee-waiver'     // OdFeeWaiver = yes + note amount
  | 'limit-increase' // OdLimit
  | 'counter-reset'  // NSFCount reset
  | 'back-on-track'; // Back-on-Track program enrollment (OdExempt block)

export type NsfOverrideReason =
  | 'courtesy'
  | 'back-on-track'
  | 'manager-discretion'
  | 'bank-error'
  | 'other';

export interface NsfOverrideInput {
  cifId: string;
  acctId: string;
  acctType: AcctType | string;
  overrideType: NsfOverrideType;
  /** USD amount for fee-waiver or new OD limit; 0 for counter-reset. */
  amount: number;
  reasonCode: NsfOverrideReason;
  notes: string;
  officerCode: string;
  /** Supervisor username (UPN) — required if officer is not a Supervisor+. */
  supervisorUpn: string;
  /** Supervisor initials. */
  supervisorInitials: string;
}

export interface UseNsfOdOverrideApi {
  run: (input: NsfOverrideInput) => Promise<MaintActionResult>;
}

function fieldsForOverride(input: NsfOverrideInput): Record<string, unknown> {
  switch (input.overrideType) {
    case 'fee-waiver':
      return { OdFeeWaiver: 'Y', OdFeeWaiverAmt: input.amount };
    case 'limit-increase':
      return { OdLimit: input.amount };
    case 'counter-reset':
      return { NSFCount: 0 };
    case 'back-on-track':
      return { OdExempt: 'Y', OdProgram: 'BOT' };
    default:
      return {};
  }
}

export function useNsfOdOverride(): UseNsfOdOverrideApi {
  const { client } = useApi();
  const emit = useEmitEvent();

  const run = useCallback(
    async (input: NsfOverrideInput): Promise<MaintActionResult> => {
      // --------------------------------------------------------------------
      // Pre-flight: emit the request event so the audit trail captures the
      // attempt even if the downstream AcctMod fails.
      // --------------------------------------------------------------------
      emit({
        eventType: 'acct.nsf.override.requested',
        eventCategory: 'cif-account',
        severity: 'warn',
        payload: {
          cifId: input.cifId,
          acctId: input.acctId,
          overrideType: input.overrideType,
          amount: input.amount,
          reasonCode: input.reasonCode,
          supervisorUpn: input.supervisorUpn,
        },
      });

      try {
        const req: AcctModRequest = {
          AcctId: input.acctId,
          AcctType: input.acctType as AcctType,
          Fields: fieldsForOverride(input),
          OperatorNumber: input.officerCode,
        };
        await client.accounts.acctMod(req);

        const today = new Date().toISOString().slice(0, 10);
        const auditNote =
          `NSF/OD Override (${input.overrideType}) on ${today}: ` +
          `$${input.amount.toFixed(2)} — reason ${input.reasonCode} ` +
          `— approved by ${input.supervisorInitials} (${input.supervisorUpn}) ` +
          `via ${input.officerCode}. Notes: ${input.notes.slice(0, 200)}`;
        try {
          await client.jxrl.post('Customer/CustMsgAdd', {
            CustId: input.cifId,
            MsgType: 'AN',
            Msg: auditNote,
            OperatorNumber: input.officerCode,
          });
        } catch {
          /* non-fatal */
        }

        emit({
          eventType: 'acct.nsf.override.applied',
          eventCategory: 'cif-account',
          severity: 'warn',
          payload: {
            cifId: input.cifId,
            acctId: input.acctId,
            overrideType: input.overrideType,
            amount: input.amount,
            reasonCode: input.reasonCode,
            supervisorUpn: input.supervisorUpn,
          },
        });

        // Compliance task for large overrides.
        if (input.amount > 500) {
          try {
            await client.retail.tasks.create({
              title: `NSF/OD Override — compliance review ($${input.amount.toFixed(2)})`,
              type: 'nsf-override-review',
              category: 'compliance',
              priority: 'High',
              assignedTo: input.supervisorUpn || input.officerCode,
              cifId: input.cifId,
              severity: 'warn',
              autoCreated: true,
              description:
                `Account ${input.acctId} received a ${input.overrideType} override of ` +
                `$${input.amount.toFixed(2)}. Reason: ${input.reasonCode}. ` +
                `Approver: ${input.supervisorInitials} (${input.supervisorUpn}).`,
            });
          } catch {
            /* non-fatal */
          }
        }

        return {
          ok: true,
          message: `Override applied to ${input.acctId}.`,
        };
      } catch (err) {
        emit({
          eventType: 'acct.nsf.override.failed',
          eventCategory: 'cif-account',
          severity: 'error',
          payload: {
            cifId: input.cifId,
            acctId: input.acctId,
            error: err instanceof Error ? err.message : String(err),
          },
        });
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
      }
    },
    [client, emit],
  );

  return { run };
}
